/* eslint-disable react-hooks/exhaustive-deps */
import {
    AppstoreFilled,
    RightOutlined,
    SearchOutlined
} from '@ant-design/icons';
import { Button, Col, Divider, Empty, Input, Row, Space, Spin, Tag, Typography } from 'antd';
import { Activity, useEffect, useEffectEvent, useRef, useState } from 'react';
import { useInView } from "react-intersection-observer";
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useDebouncedCallback } from 'use-debounce';
import Categories from '../../../components/ui/Categories';
import LoadingCard from '../../../components/ui/LoadingCard';
import ProductCard from '../../../components/ui/ProductCard';
import { addFavourite, getPublicProductsWithPagination, removeFavourite } from '../../../redux/actions/products';
import { clearFilters, removeCategory, setSearch } from '../../../redux/slices/filtersSlice';
import { store } from '../../../redux/store';
import { getRoutes } from '../../../routes/Routes';


const { Text } = Typography;

const PublicCatalogue = () => {
    const navigate = useNavigate();
    const { ref, inView } = useInView({
        rootMargin: '1000px',
    });

    const { search, category, customer, favourite, recent, offer } = useSelector(
        (state) => state.filtersSlice
    );
    const { products, pagination } = useSelector((state) => state.productsSlice);

    const [filterOpen, setFilterOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [loadingMore, setLoadingMore] = useState(false);
    const hasFetched = useRef(false);

    const hasMore = !!products?.length && pagination.page < pagination.totalPages;
    const hasActiveFilters = [category?.length, search, customer, favourite, offer].some(Boolean);

    const fetchProducts = useEffectEvent(async (params = {}, append = false, showLoading = true) => {
        if (showLoading) setLoading(true);
        try {
            await getPublicProductsWithPagination({
                body: {
                    searchText: params.searchText ?? store.getState().filtersSlice.search,
                    customer_id: params.customer_id ?? store.getState().filtersSlice.customer,
                    category: params.category ?? store.getState().filtersSlice.category,
                    favourite: params.favourite ?? store.getState().filtersSlice.favourite,
                    offer: params.offer ?? store.getState().filtersSlice.offer,
                    recent: params.recent ?? store.getState().filtersSlice.recent,
                    page: params.page ?? 1,
                },
                isAppend: append,
            });

        } finally {
            if (showLoading) setLoading(false);
        }
    });

    const fetchProductsDebounced = useDebouncedCallback((params = {}) => {
        fetchProducts(params);
    }, 400);

    const handleSearchChange = (e) => {
        const value = e.target.value;
        store.dispatch(setSearch(value));
        fetchProductsDebounced({ searchText: value });
    };

    const handleApplyFilters = () => {
        setFilterOpen(false);
        fetchProducts();
    };

    const handleRemoveCategory = (filter) => {
        const updated = category.filter((c) => c !== filter);
        store.dispatch(removeCategory(filter));
        fetchProducts({ category: updated });
    };

    const handleToggleFav = (product) => {
        const isFavourite = !!product?.favourite;
        const filters = { searchText: search, customer_id: customer, category, favourite, recent };
        isFavourite
            ? removeFavourite({ product_netsuite_id: product?.netsuite_id }, filters)
            : addFavourite({ product_netsuite_id: product?.netsuite_id }, filters);
    };

    const getMoreProducts = useEffectEvent(async () => {
        if (!hasMore || loadingMore) return;
        setLoadingMore(true);
        try {
            await fetchProducts(
                {
                    searchText: search,
                    customer_id: customer,
                    category,
                    favourite,
                    recent,
                    offer,
                    page: pagination.page + 1,
                },
                true,
                false
            );
        } finally {
            setLoadingMore(false);
        }
    });

    const handleClearAll = () => {
        store.dispatch(clearFilters());
        fetchProducts({
            searchText: '',
            customer_id: null,
            favourite: 0,
            category: [],
            offer: 0,
            recent: 0,
        });
    };

    const handleCreateOrder = useEffectEvent(async () => {
        return navigate(getRoutes().login.route());
    });


    useEffect(() => {
        if (!hasFetched.current) {
            hasFetched.current = true;
            fetchProducts();
        }
    }, []);

    useEffect(() => {
        if (inView && hasMore && !loadingMore) {
            getMoreProducts();
        }
    }, [inView]);

    return (
        <div className="catalogue-page">
            <div className="flex justify-content-between align-items-center gap-2">
                <div className="mb-3">
                    <Typography.Title level={3} className='page-title'>
                        Product Catalogue
                    </Typography.Title>
                    <Text type="secondary">Browse and manage your product inventory</Text>
                </div>
            </div>
            <div className="flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
                <div className="flex align-items-center flex-wrap gap-2 flex-1">
                    <Input
                        size="large"
                        placeholder="Search products by name or code"
                        prefix={<SearchOutlined />}
                        value={search}
                        onChange={handleSearchChange}
                        allowClear
                        style={{ maxWidth: 350 }}
                    />
                </div>

                <Space>
                    {hasActiveFilters && (
                        <Button
                            variant="link"
                            color="danger"
                            onClick={handleClearAll}
                            className="flex-shrink-0"
                        >
                            Clear All
                        </Button>
                    )}

                    <Button
                        size="large"
                        variant="filled"
                        color="default"
                        icon={<AppstoreFilled />}
                        onClick={() => setFilterOpen(true)}
                    >
                        Categories {!!category?.length && `(${category.length})`} <RightOutlined />
                    </Button>
                </Space>
            </div>

            {category?.length > 0 && (
                <div className="flex align-items-center mb-3 gap-2 overflow-x-auto no-scrollbar py-1">
                    <Text className="flex-shrink-0">Active Filters:</Text>
                    <div className="flex gap-2 flex-nowrap">
                        {category.map((filter, i) => (
                            <Tag
                                color="#f5f5f5"
                                className="filter-tag shadow-1 whitespace-nowrap"
                                key={i}
                                closable
                                onClose={() => handleRemoveCategory(filter)}
                            >
                                {filter}
                            </Tag>
                        ))}
                    </div>
                </div>
            )}

            <Divider />

            <div className="catalogue p-2">
                {loading ? (
                    <div className="flex justify-content-center align-items-center gap-3 py-8">
                        <Spin />
                        <Text type="secondary" className="text-lg">
                            Loading products, please wait...
                        </Text>
                    </div>
                ) : products?.length ? (
                    <Row gutter={[5, 5]}>
                        {products.map((product, i) => (
                            <Col xs={12} sm={8} md={8} lg={4} key={product?.netsuite_id || i}>
                                <ProductCard
                                    product={product}
                                    onFavToggle={handleToggleFav}
                                    onCreateOrder={handleCreateOrder}
                                    showFav
                                />
                            </Col>
                        ))}

                        {loadingMore &&
                            Array.from({ length: 3 }).map((_, i) => (
                                <Col xs={12} sm={8} md={8} lg={4} key={`loading-${i}`}>
                                    <LoadingCard />
                                </Col>
                            ))}
                    </Row>
                ) : (
                    <div className="flex justify-content-center align-items-center">
                        <Empty
                            image={Empty.PRESENTED_IMAGE_SIMPLE}
                            description={<Text type="secondary">No products found</Text>}
                        />
                    </div>
                )}
            </div>

            <div ref={ref} className='load-more' />

            <Activity mode={filterOpen ? 'visible' : 'hidden'}>
                <Categories
                    open={filterOpen}
                    onClose={() => setFilterOpen(false)}
                    onApply={handleApplyFilters}
                />
            </Activity>
        </div>
    );
};

export default PublicCatalogue;
